import React, { useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, TextField, Tooltip } from '@mui/material';
import { getStores } from '../api/stockLedger';
import { toInventoryList } from '../api/inventory-request';
import { storeDisplayName } from '../utils/storeEntity';
import { filterStoresByAssignment, storeRowId } from '../utils/assignedStores';
import useActiveStore from '../hooks/useActiveStore';

/**
 * Header/sidebar store picker. Only renders when the user has 2+ assigned stores.
 * Pages read the same activeStoreId via useActiveStore.
 */
export default function InvStoreSwitcher({ variant = 'sidebar' }) {
  const { assignedStoreIds, activeStoreId, setActiveStoreId, isStoreScoped } = useActiveStore();
  const [stores, setStores] = useState([]);

  useEffect(() => {
    if (assignedStoreIds.length < 2) return undefined;
    let cancelled = false;
    getStores()
      .then((data) => {
        if (!cancelled) setStores(toInventoryList(data));
      })
      .catch(() => {
        if (!cancelled) setStores([]);
      });
    return () => { cancelled = true; };
  }, [assignedStoreIds]);

  const options = useMemo(
    () => filterStoresByAssignment(stores, assignedStoreIds).map((s) => ({
      value: storeRowId(s),
      label: s.store_name || storeDisplayName(s),
    })),
    [stores, assignedStoreIds],
  );

  if (!isStoreScoped || assignedStoreIds.length < 2) return null;

  const isSidebar = variant === 'sidebar';
  const select = (
    <TextField
      select
      size="small"
      label={isSidebar ? undefined : 'Store'}
      value={options.some((o) => o.value === String(activeStoreId)) ? String(activeStoreId) : ''}
      onChange={(e) => setActiveStoreId(e.target.value)}
      fullWidth
      SelectProps={{ displayEmpty: true }}
      sx={isSidebar ? {
        '& .MuiInputBase-root': {
          color: '#fff',
          fontSize: 13,
          backgroundColor: 'rgba(255,255,255,0.1)',
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.35)' },
        '& .MuiSvgIcon-root': { color: '#fff' },
      } : {
        minWidth: 220,
        maxWidth: 320,
      }}
    >
      {options.length === 0 && (
        <MenuItem value={String(activeStoreId || '')} disabled>
          Loading stores…
        </MenuItem>
      )}
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
      ))}
    </TextField>
  );

  if (isSidebar) {
    return (
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Box sx={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.55)',
          mb: 0.75,
        }}
        >
          Active store
        </Box>
        {select}
      </Box>
    );
  }

  return (
    <Tooltip title="Switch assigned store" placement="bottom">
      <Box sx={{ ml: 'auto', minWidth: 220 }}>{select}</Box>
    </Tooltip>
  );
}
